import PaymentManager from '../api/PaymentManager';
import paymentLog from './PaymentLogger';

/** Asked again this long before Stripe stops taking the secret. */
export const CUSTOMER_SESSION_REFRESH_MARGIN_MS = 60_000;

export interface CustomerSessionAnswer {
  clientSecret: null | string;
  /** Unix seconds, as Stripe gives it. */
  expiresAt: null | number;
}

const answers = new Map<string, CustomerSessionAnswer>();
const inFlight = new Map<string, Promise<CustomerSessionAnswer>>();

/** One customer at one restaurant: a secret is never handed to another customer, or another account. */
export const customerSessionKey = (companyId: null | number | undefined, userId: null | number | undefined) =>
  companyId && userId ? `${companyId}:${userId}` : null;

const isFresh = (answer: CustomerSessionAnswer | undefined): boolean =>
  Boolean(answer?.clientSecret) &&
  answer.expiresAt !== null &&
  Date.now() < answer.expiresAt * 1000 - CUSTOMER_SESSION_REFRESH_MARGIN_MS;

const ask = (companyId: number): Promise<CustomerSessionAnswer> =>
  PaymentManager.createStripeCustomerSession(companyId)
    .then(({ success, response, status }): CustomerSessionAnswer => {
      if (!success) {
        paymentLog.warn('stripe', 'customer_session_failed', { company_id: companyId, status });
      }

      return {
        clientSecret: success ? (response?.data?.client_secret ?? null) : null,
        expiresAt: success ? (response?.data?.expires_at ?? null) : null,
      };
    })
    .catch((): CustomerSessionAnswer => ({ clientSecret: null, expiresAt: null }));

/**
 * The CustomerSession a logged-in customer's Stripe Elements are created with, kept for the page's
 * life and shared by whoever asks: the checkout step asks ahead (prefetch), so by the time the payment
 * screen opens the answer is in and its Elements are created at once, with the save box, instead of
 * after one more backend round trip. A secret is kept until shortly before Stripe expires it; an
 * answer without one (a guest's restaurant, a refusal) is not kept, and the next screen asks again.
 */
export const StripeCustomerSessionCache = {
  /** A secret still good for a while, if one is in. */
  peek(key: null | string): CustomerSessionAnswer | undefined {
    const answer = key === null ? undefined : answers.get(key);

    return isFresh(answer) ? answer : undefined;
  },

  /**
   * The kept secret when it is still good, otherwise one request, shared by everyone asking meanwhile.
   * Without a key (the customer is not known yet) the answer is asked for and not kept.
   */
  fetch(key: null | string, companyId: number): Promise<CustomerSessionAnswer> {
    if (key === null) {
      return ask(companyId);
    }

    const kept = StripeCustomerSessionCache.peek(key);

    if (kept) {
      return Promise.resolve(kept);
    }

    const pending = inFlight.get(key);

    if (pending) {
      return pending;
    }

    const request = ask(companyId)
      .then((answer) => {
        if (isFresh(answer)) {
          answers.set(key, answer);
        }

        return answer;
      })
      .finally(() => inFlight.delete(key));

    inFlight.set(key, request);

    return request;
  },

  /** Asked ahead of the payment screen; nothing is waited for. */
  prefetch(key: null | string, companyId: null | number | undefined): void {
    if (key === null || !companyId) {
      return;
    }

    void StripeCustomerSessionCache.fetch(key, companyId);
  },

  /** A secret the Elements could not load with is not handed out again. */
  forget(key: null | string, clientSecret: null | string): void {
    if (key !== null && answers.get(key)?.clientSecret === clientSecret) {
      answers.delete(key);
    }
  },

  /** Tests only. */
  clear(): void {
    answers.clear();
    inFlight.clear();
  },
};
